import classNames from "classnames";
import { ArrowDownIcon } from "@icons";
import type { ResolvedAnatomyBinding } from "./componentAnatomy";
import {
    bindingReader,
    Part,
    type AnatomyPartHighlight,
} from "./componentAnatomyParts";
import {
    PickerSchematicRow,
    pickerTextStyle,
} from "./ComponentAnatomySchematicPickerRow";
import pickerStyles from "./ComponentAnatomySchematicPicker.module.scss";
import styles from "./ComponentAnatomyView.module.scss";

/** Sandbox toggles the picker plate mirrors. */
export interface PickerSchematicContent {
    variantId: string;
    hasSections: boolean;
    hasFooter: boolean;
}

const defaultContent: PickerSchematicContent = {
    variantId: "single",
    hasSections: false,
    hasFooter: false,
};

/** X-ray plate for Picker: the field trigger above an open popup surface. */
export const PickerSchematic = ({
    bindings,
    highlight,
    content = defaultContent,
}: {
    bindings: readonly ResolvedAnatomyBinding[];
    highlight: AnatomyPartHighlight;
    content?: PickerSchematicContent;
}) => {
    const read = bindingReader(bindings);
    const radius = read("border", "border-radius");
    const ringSpread = read("focusRing", "box-shadow (spread)");
    const ringColor = read("focusRing", "box-shadow (color)");
    const isTree = content.variantId === "tree";
    const rowProps = { read, highlight, variantId: content.variantId };
    // The real separator's negative inline margin spans the section inset; on
    // the plate the section is its own box, so the pull is not re-applied.
    const sectionStyle = {
        paddingInline: read("section", "padding-inline"),
        rowGap: read("section", "row-gap"),
    };

    return (
        <span
            className={classNames(
                styles.schematic,
                pickerStyles.pickerSchematic,
            )}
        >
            <span
                className={classNames(
                    styles.schematic,
                    styles.buttonSchematic,
                    pickerStyles.pickerTrigger,
                )}
                style={{ height: read("background", "height") }}
            >
                {ringSpread !== undefined && ringColor !== undefined && (
                    <Part
                        partId="focusRing"
                        highlight={highlight}
                        className={styles.buttonRing}
                        hitInset={`calc(-1 * ${ringSpread})`}
                        style={{
                            borderRadius: radius,
                            boxShadow: `0 0 0 ${ringSpread} ${ringColor}`,
                        }}
                    />
                )}
                <Part
                    partId="border"
                    highlight={highlight}
                    className={styles.buttonBorder}
                    style={{
                        borderColor: read("border", "border-color"),
                        borderWidth: read("border", "border-width"),
                        borderRadius: radius,
                    }}
                />
                <Part
                    partId="background"
                    highlight={highlight}
                    className={styles.buttonBackground}
                    style={{
                        backgroundColor: read("background", "background-color"),
                        borderRadius: radius,
                    }}
                />
                <span
                    className={classNames(
                        styles.buttonContent,
                        pickerStyles.pickerTriggerContent,
                    )}
                    style={{
                        paddingInline: read("background", "padding-inline"),
                    }}
                >
                    <Part
                        partId="label"
                        highlight={highlight}
                        className={styles.buttonLabel}
                        style={{
                            color: read("label", "color"),
                            ...pickerTextStyle(read, "label"),
                        }}
                    >
                        Operating account
                    </Part>
                    <Part
                        partId="glyph"
                        highlight={highlight}
                        className={pickerStyles.pickerChevron}
                        style={{
                            width: read("glyph", "width"),
                            height: read("glyph", "height"),
                            color: read("glyph", "color"),
                        }}
                    >
                        <ArrowDownIcon />
                    </Part>
                </span>
            </span>
            <Part
                partId="surface"
                highlight={highlight}
                className={pickerStyles.pickerSurface}
                style={{
                    backgroundColor: read("surface", "background-color"),
                    borderColor: read("surface", "border-color"),
                    borderWidth: read("surface", "border-width"),
                    borderRadius: read("surface", "border-radius"),
                    boxShadow: read("surface", "box-shadow"),
                    padding: read("surface", "padding"),
                    rowGap: read("surface", "row-gap"),
                }}
            >
                <Part
                    partId="section"
                    highlight={highlight}
                    className={pickerStyles.pickerSection}
                    style={sectionStyle}
                >
                    {content.hasSections && (
                        <Part
                            partId="sectionTitle"
                            highlight={highlight}
                            className={pickerStyles.pickerSectionTitle}
                            style={{
                                color: read("sectionTitle", "color"),
                                padding: read("sectionTitle", "padding"),
                                ...pickerTextStyle(read, "sectionTitle"),
                            }}
                        >
                            Bank accounts
                        </Part>
                    )}
                    {isTree ? (
                        <>
                            <PickerSchematicRow
                                {...rowProps}
                                label="Expenses"
                                isStateRow
                                isSelected={false}
                                hasExpandToggle
                            />
                            <PickerSchematicRow
                                {...rowProps}
                                label="Rent"
                                isStateRow={false}
                                isSelected
                                depth={1}
                                treePosition="top"
                            />
                            <PickerSchematicRow
                                {...rowProps}
                                label="Utilities"
                                isStateRow={false}
                                isSelected={false}
                                depth={1}
                                treePosition="middle"
                            />
                            <PickerSchematicRow
                                {...rowProps}
                                label="Software"
                                isStateRow={false}
                                isSelected={false}
                                depth={1}
                                treePosition="bottom"
                            />
                        </>
                    ) : (
                        <>
                            <PickerSchematicRow
                                {...rowProps}
                                label="Operating account"
                                isStateRow
                                isSelected
                            />
                            <PickerSchematicRow
                                {...rowProps}
                                label="Payroll account"
                                isStateRow={false}
                                isSelected={false}
                            />
                            <PickerSchematicRow
                                {...rowProps}
                                label="Savings"
                                isStateRow={false}
                                isSelected={false}
                            />
                        </>
                    )}
                </Part>
                {content.hasFooter && (
                    <>
                        <Part
                            partId="separator"
                            highlight={highlight}
                            className={pickerStyles.pickerSeparator}
                            style={{
                                height: read("separator", "height"),
                                backgroundColor: read(
                                    "separator",
                                    "background-color",
                                ),
                                marginBlock: read("separator", "margin-block"),
                            }}
                        />
                        <Part
                            partId="section"
                            highlight={highlight}
                            className={pickerStyles.pickerSection}
                            style={sectionStyle}
                        >
                            <PickerSchematicRow
                                {...rowProps}
                                label="Add account"
                                isStateRow={false}
                                isSelected={false}
                            />
                        </Part>
                    </>
                )}
            </Part>
        </span>
    );
};
